import { Close } from "@mui/icons-material";
import { IconButton, InputBase, InputBaseProps, Paper } from "@mui/material";
import React from "react";
import { Time } from "../../Helper/Time";

export interface TimeInputInnerProps {
	value: number | null
	unit?: number
	inputProps?: InputBaseProps
	onChange: (value: number | null) => void
	onPrev?: () => void
	onNext?: () => void
}

const TimeInputInner = React.forwardRef<HTMLInputElement, TimeInputInnerProps>(
	function TimeInputInner({
		value,
		unit,
		inputProps,
		onChange,
		onPrev,
		onNext,
	}, ref) {
		const [disp, setDisp] = React.useState<string>("")
		const [isOverride, setIsOverride] = React.useState(true)

		const doChange = React.useCallback((newValue: number, unit?: number) => {
			if (unit) {
				newValue = Math.round(newValue / unit) * unit
			}
			onChange(newValue)
		}, [onChange])

		const handlerKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
			const newValue = value === null ? 0 : value;
			switch (e.key) {
				case "0":
				case "1":
				case "2":
				case "3":
				case "4":
				case "5":
				case "6":
				case "7":
				case "8":
				case "9":
					doChange(isOverride ? parseInt(e.key) : newValue * 10 + parseInt(e.key))
					break;
				case "Backspace":
				case "Delete":
					doChange(0)
					break;
				case "ArrowUp":
					doChange(newValue + (unit ?? 1), unit ?? 1)
					break;
				case "ArrowDown":
					// if( value > 0 ) {
					doChange(newValue - (unit ?? 1), unit ?? 1)
					// }
					break;
				case "ArrowRight":
					if (onNext) {
						onNext()
					}
					break;
				case "ArrowLeft":
					if (onPrev) {
						onPrev()
					}
					break;
			}
			setIsOverride(false)
		}, [value, doChange, onNext, onPrev, isOverride, unit])

		const handleFocus = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
			setIsOverride(true)
			e.target.select();
		}, [])

		const handleBlur = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
			if (
				e.target.value !== "" &&
				isNaN(e.target.value as any) === false
			) {
				let v = parseInt(e.target.value);
				if (v < 10) {
					setDisp("0" + v);
				}
			}
		}, [])

		React.useEffect(() => {
			if (value === null) {
				setDisp("")
			} else {
				setDisp(value < 10 ? `0${value}` : `${value}`)
			}
		}, [value])

		return (
			<InputBase
				{...inputProps}
				inputRef={ref}
				type="tel"
				value={disp}
				onKeyDown={handlerKeyDown}
				// onChange={handleChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
			/>
		)
	}
)

export interface TimeInputProps {
	value: Time | null;
	onChange: (newValue: Time | null) => void;
	required?: boolean;
	onPrev?: () => void;
	onNext?: () => void;
	min?: Time;
	max?: Time;
	unit?: number;
	disableClear?: boolean;
}


const TimeInput = React.forwardRef<HTMLElement, TimeInputProps>(
	function TimeInput({
		value,
		onChange,
		required,
		onPrev,
		onNext,
		min,
		max,
		unit,
		disableClear,
	}, ref) {

		const refHH = React.useRef<HTMLInputElement>(null)
		const refMM = React.useRef<HTMLInputElement>(null)

		const [inputColor, setInputColor] = React.useState<InputBaseProps["color"]>("primary")

		const doChange = React.useCallback((newValue: Time | null) => {
			onChange(newValue)
		}, [onChange])

		React.useEffect(() => {
			if (value) {
				if (min && value.toSec() < min.toSec()) {
					setInputColor("error")
					return
				}
				if (max && value.toSec() > max.toSec()) {
					setInputColor("error")
					return
				}
				if (unit && value.minute % unit > 0) {
					setInputColor("error")
					return
				}
			}
			setInputColor("primary")
		}, [value, min, max, unit])

		const onChangeHH = React.useCallback((newValue) => {
			if (newValue === null) {
				doChange(null)
			} else {
				let time = new Time(value ?? "00:00");
				time.hour = newValue
				doChange(time);
			}
		}, [value, doChange])

		const onChangeMM = React.useCallback((newValue) => {
			if (newValue === null) {
				doChange(null)
			} else {
				let time = new Time(value ?? "00:00");
				time.minute = newValue
				doChange(time);
			}
		}, [value, doChange])

		return (
			<>
				<Paper
					variant="outlined"
					sx={{
						p: "2px 4px",
						display: "flex",
						alignItems: "center",
						width: 120,
						borderColor: inputColor === "error" ? "error.main" : undefined
					}}
				>
					<TimeInputInner
						ref={refHH}
						inputProps={{
							sx: {
								flex: 1
							},
							placeholder: "HH",
							inputProps: {
								sx: {
									textAlign: "center"
								}
							},
							required: required,
							color: inputColor,
						}}
						value={value ? value.hour : null}
						onChange={onChangeHH}
						onPrev={() => {
							if (onPrev) {
								onPrev()
							}
						}}
						onNext={() => {
							if (refMM.current) {
								refMM.current.select()
							}
						}}
					/>
					{":"}
					<TimeInputInner
						ref={refMM}
						inputProps={{
							sx: {
								flex: 1
							},
							placeholder: "MM",
							inputProps: {
								sx: {
									textAlign: "center"
								}
							},
							required: required,
							color: inputColor,
						}}
						value={value ? value.minute : null}
						unit={(unit && unit < 60 && 0 < unit) ? unit : undefined}
						onChange={onChangeMM}
						onPrev={() => {
							if (refHH.current) {
								refHH.current.select()
							}
						}}
						onNext={() => {
							if (onNext) {
								onNext()
							}
						}}
					/>
					{!disableClear &&
						<IconButton
							onClick={() => {
								doChange(null)
							}}>
							<Close />
						</IconButton>
					}
				</Paper>
				{/* <OutlinedInput
                    size="small"
                    value={strValue ? strValue : "--:--"}
                    onChange={newValue => {
                        setStrValue(newValue.target.value)
                    }}
                    endAdornment={
                        <InputAdornment position="end">
                        </InputAdornment>
                    }
                /> */}
			</>
		);
	}
);

export default TimeInput;
